/**
 * Pathless layout for every signed-in screen. beforeLoad makes sure the
 * session exists and every onboarding gate is cleared, otherwise it
 * redirects (login, change password, terms, welcome, account closed).
 * AppShell brings the rail or the phone bars, the banners, the command
 * palette and the live-update connection; the screen renders into <main>.
 */
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { AppShell } from '@/components/AppShell';
import { gateRoute, sessionQuery } from '@/lib/session';

/** Signed-in pages the public site has a twin of. A visitor who types /help
 *  or /whats-new reads the public page (the same articles, the same
 *  release notes; a Help article's #slug comes along) instead of meeting
 *  Log in, as /terms and /privacy already do. */
const PUBLIC_TWIN: Record<string, '/help-centre' | '/changelog'> = {
  '/help': '/help-centre',
  '/whats-new': '/changelog',
};

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const s = await context.queryClient.ensureQueryData(sessionQuery);
    if (!s.authenticated) {
      const twin = PUBLIC_TWIN[location.pathname.replace(/\/+$/, '')];
      if (twin) throw redirect({ to: twin, hash: location.hash || undefined, replace: true });
      throw redirect({ to: '/login', search: { next: location.href } });
    }
    const gate = gateRoute(s);
    if (gate) throw redirect({ to: gate });
    return { session: s };
  },
  component: AppLayout,
});

function AppLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
